import React, { useRef, useState, useEffect } from 'react';
import { Newspaper, Award, Camera, Settings, Monitor, ChevronLeft, ChevronRight, Image } from 'lucide-react';

const MobileDock = ({ activeTab, onTabChange, hasNewNews }) => {
    const scrollRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);

    const updateArrows = () => {
        const el = scrollRef.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 0);
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        el.addEventListener('scroll', updateArrows, { passive: true });
        updateArrows();
        return () => el.removeEventListener('scroll', updateArrows);
    }, []);

    const dockItems = [
        { id: 'video', label: 'Media', icon: Monitor },
        { id: 'photos', label: 'Photos', icon: Image },
        { id: 'images', label: 'Newsroom', icon: Newspaper },

        { id: 'members', label: 'Members', icon: Award },
        { id: 'instagram', label: 'Instagram', icon: Camera, link: 'https://www.instagram.com/welcometoparadise.us/' },
        { id: 'settings', label: 'Settings', icon: Settings },
    ];

    return (
        <div className="h-20 bg-[#e8e6df] border-t border-black/10 flex items-center justify-between px-1 z-50 shadow-[0_-4px_10px_rgba(0,0,0,0.1)] flex-shrink-0">
            {/* Left Scroll Arrow */}
            <button
                onClick={() => scrollRef.current?.scrollBy({ left: -120, behavior: 'smooth' })}
                className={`w-8 h-full flex items-center justify-center text-[#333] transition-opacity ${canScrollLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            >
                <ChevronLeft size={20} />
            </button>

            {/* Scrollable Icons Area */}
            <div ref={scrollRef} className="flex-1 flex overflow-x-auto items-center justify-start scrollbar-hide h-full">
                {dockItems.map((item) => {
                    const isActive = activeTab === item.id;
                    const showDot = item.id === 'images' && hasNewNews;
                    return (
                        <button
                            key={item.id}
                            onClick={() => item.link ? window.open(item.link, '_blank') : onTabChange(item.id)}
                            className={`group relative flex flex-col items-center justify-center min-w-[80px] h-full transition-all duration-200 ease-out ${isActive ? 'bg-[#dcdad3] shadow-[inset_0_2px_4px_rgba(0,0,0,0.05)]' : 'active:bg-[#dcdad3]'
                                }`}
                        >
                            {/* Active Indicator */}
                            {isActive && (
                                <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#AF2E1B]"></div>
                            )}

                            {/* Button Bevel/Shadow */}
                            <div className="absolute inset-0 shadow-[inset_-1px_0_0_rgba(0,0,0,0.05)] pointer-events-none"></div>

                            <div className="relative z-10 mb-1">
                                <div className={`relative drop-shadow-[0_1px_0_rgba(255,255,255,0.5)] transition-colors ${isActive ? 'text-[#AF2E1B]' : 'text-[#333333]'
                                    }`}>
                                    <item.icon size={24} strokeWidth={isActive ? 2 : 1.5} />
                                    {showDot && (
                                        <div className="absolute -top-0.5 -right-1 w-1.5 h-1.5 bg-[#ff4400] rounded-full ring-1 ring-[#e8e6df]"></div>
                                    )}
                                </div>
                            </div>
                            <span className={`relative z-10 text-[9px] font-sans font-bold uppercase tracking-wider group-hover:text-black transition-colors ${isActive ? 'text-[#AF2E1B]' : 'text-[#444444]'
                                }`}>
                                {item.label}
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* Right Scroll Arrow */}
            <button
                onClick={() => scrollRef.current?.scrollBy({ left: 120, behavior: 'smooth' })}
                className={`w-8 h-full flex items-center justify-center text-[#333] transition-opacity ${canScrollRight ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            >
                <ChevronRight size={20} />
            </button>
        </div>
    );
};

export default MobileDock;
